import { ColumnDef } from '@tanstack/react-table'
import i18next from 'i18next'
import _ from 'lodash'

import { FlexBox } from 'components/Box'
import { COLUMN_SIZE } from 'components/Table/constants'
import { Alert } from 'features/alerts/types/alert'
import { AdUnitBidderAlert } from 'features/globalSetup/api/types/adUnitBidderAlert'
import { AdUnitBidderAlerts } from 'features/inventory/ad-unit/forms/PrebidStackForm/sections/BiddersSection/components/AdUnitBidderAlerts'
import { EnableBidderToggleCell } from '../cells/EnableBidderToggleCell'
import { AdUnitsForTable, Params, UseColumnsProps } from '../types'

type Props = Params & Pick<UseColumnsProps, 's2s' | 'workspaceStatus'>

export const createEnableBidderColumns = ({
  cs,
  s2s,
  workspaceStatus,
}: Props): ColumnDef<AdUnitsForTable> => {
  const t = (path: string): string =>
    i18next.t(
      `features/globalSetup:prebid.bidders.edit.bidParamsAndConnection.adUnitsTable.${path}`,
    ) as string

  const { csRequiredParamsNames } = cs
  const { s2sParams, s2sRequiredParamsNames } = s2s

  const csColumn: ColumnDef<AdUnitsForTable> = {
    accessorKey: 'csEnabled',
    cell: context => {
      const {
        row: {
          original: { alerts },
        },
      } = context

      return (
        <FlexBox axis='x'>
          <EnableBidderToggleCell
            cellContext={context}
            kind='cs'
            requiredParams={csRequiredParamsNames}
            workspaceStatus={workspaceStatus}
          />

          {alerts.length > 0 && (
            <AdUnitBidderAlerts
              alerts={alerts.filter((alert: Alert<AdUnitBidderAlert>): boolean =>
                alert.name.startsWith('cs'),
              )}
            />
          )}
        </FlexBox>
      )
    },
    header: t('enableBidder.cs.label'),
    meta: {
      centered: true,
      tooltip: { content: t('enableBidder.cs.tooltip') },
    },
    size: COLUMN_SIZE.SM,
  }

  const s2sColumn: ColumnDef<AdUnitsForTable> | null = _.isEmpty(s2sParams)
    ? null
    : {
        accessorKey: 's2sEnabled',
        cell: context => {
          const {
            row: {
              original: { alerts },
            },
          } = context

          return (
            <FlexBox axis='x'>
              <EnableBidderToggleCell
                cellContext={context}
                kind='s2s'
                requiredParams={s2sRequiredParamsNames}
                workspaceStatus={workspaceStatus}
              />

              {alerts.length > 0 && (
                <AdUnitBidderAlerts
                  alerts={alerts.filter((alert: Alert<AdUnitBidderAlert>): boolean =>
                    alert.name.startsWith('s2s'),
                  )}
                />
              )}
            </FlexBox>
          )
        },
        header: t('enableBidder.s2s.label'),
        meta: {
          centered: true,
          tooltip: { content: t('enableBidder.s2s.tooltip') },
        },
        size: COLUMN_SIZE.SM,
      }

  const columns: ColumnDef<AdUnitsForTable>[] = [csColumn, s2sColumn].filter(el => el !== null)

  return {
    columns,
    header: t('enableBidder.group'),
  }
}
